import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { KeyDownWrapper } from "components/DesignSystem/KeydownWrapper/KeydownWrapper";
import { NoteCard } from "components/DesignSystem/Notes/NoteCard";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import {
  BareInput,
  TextInput,
} from "components/DesignSystem/TextInput/TextInput";
import { OutlinedPaperPlaneRight } from "components/icons/OutlinedPaperPlaneRight";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import {
  useAddFinancialClosingCommentsMutation,
  useGetFinancialClosingCommentsQuery,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { formatNumber } from "utils/formatNumber";
import { useState } from "react";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

export const Comments = ({
  financialClosingId,
}: {
  financialClosingId: string;
}) => {
  const [addFinancialClosingComments, { isLoading }] =
    useAddFinancialClosingCommentsMutation();
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const [currentComment, setCurrentComment] = useState("");
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const {
    data: comments,
    isLoading: loading,
    isSuccess,
  } = useGetFinancialClosingCommentsQuery(
    {
      entityId: entityId,
      financialClosingId: financialClosingId,
    },
    {
      skip: !financialClosingId || !entityId,
    }
  );

  const onSubmit = async () => {
    if (!currentComment) return;

    try {
      await addFinancialClosingComments({
        entityId,
        financialClosingId,
        description: currentComment,
      }).unwrap();
      setCurrentComment("");
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <Accordion.Root defaultValue="COMMENT" collapsible type="single">
      <Accordion.Item value="COMMENT">
        <Accordion.Trigger>
          Comments (
          {Array.isArray(comments?.notes)
            ? formatNumber(comments.notes.length)
            : 0}
          )
        </Accordion.Trigger>
        <Accordion.Content>
          <Async.Root isEmpty={false} isLoading={loading} isSuccess={isSuccess}>
            <Async.Empty>
              <></>
            </Async.Empty>
            <Async.Success>
              <span>
                <KeyDownWrapper>
                  <div className="t-overflow-auto t-flex t-flex-col t-gap-2 t-max-h-80">
                    <div className="t-flex t-flex-col t-gap-2">
                      {comments?.notes.map((note) => (
                        <NoteCard note={note} key={note.uuid} />
                      ))}
                    </div>
                    <div
                      onSubmit={(e) => e.preventDefault()}
                      className={classNames(
                        "t-w-full t-m-0 t-sticky t-bottom-0"
                      )}
                    >
                      <BareInput
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !e.shiftKey &&
                            hasWritePermission
                          ) {
                            e.preventDefault();
                            onSubmit();
                          }
                        }}
                        placeholder="Add your comment here"
                        name="description"
                        onChange={(e) => setCurrentComment(e.target.value)}
                        value={currentComment}
                        customSize="large"
                        disabled={!hasWritePermission}
                        rightComponent={
                          <Button
                            onClick={onSubmit}
                            customType="ghost_icon"
                            isLoading={isLoading}
                            disabled={isLoading || !hasWritePermission}
                          >
                            <span className="t-text-text-30">
                              <OutlinedPaperPlaneRight size="20" />
                            </span>
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </KeyDownWrapper>
              </span>
            </Async.Success>
          </Async.Root>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
